import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaInfoCircle } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { TbDownload } from 'react-icons/tb';
import Card from '../../components/cards/Card';
import AnswerPercentageGraph from '../../components/graphs/AnswerPercentageGraph';
import AttendanceGraph from '../../components/graphs/AttendanceGraph';
import DatePicker from '../../components/forms/DatePicker';
import useEvents from '../../../hooks/community/useEvents';
import { EventParticipationAnswer, EventType, EventViewType } from '../../../types/event';
import eventService from '../../../services/community/eventService';
import PodiumGraph from '../../components/graphs/PodiumGraph';
import Button from '../../components/buttons/Button';
import { downloadAnalytics } from '../../../utils/fileUtils';
import { formatDateForFileName } from '../../../utils/stringUtils';
import { setEvents } from '../../../redux/slices/eventSlice';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import TabButtons from '../../components/buttons/TabButtons';
import Labeled from '../../components/misc/Labeled';
import PrimaryOverlay from '../../components/overlays/PrimaryOverlay';
import PremiumRequiredCard from '../../components/cards/PremiumRequiredCard';

const MOCK_EVENTS = [
  {
    id: 1,
    communityId: 101,
    title: 'Networking Meetup',
    memberCount: 15,
    description: 'A local networking event for professionals.',
    location: 'San Francisco',
    startTime: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
    type: EventType.OTHER,
    isDatePicker: false,
    participants: [
      {
        id: 1,
        firstName: 'John',
        lastName: 'Doe',
        answer: EventParticipationAnswer.YES,
        description: 'Software engineer with 10 years of experience.',
        picture: 'https://example.com/john_doe.jpg',
      },
      {
        id: 2,
        firstName: 'Jane',
        lastName: 'Smith',
        answer: EventParticipationAnswer.MAYBE,
        description: 'Marketing specialist with a passion for branding.',
        picture: 'https://example.com/jane_smith.jpg',
      },
    ],
  },
  {
    id: 2,
    communityId: 102,
    title: 'Tech Conference',
    memberCount: 100,
    description: 'Annual tech conference for startups and innovators.',
    location: 'New York',
    startTime: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
    type: EventType.OTHER,
    isDatePicker: false,
    participants: [
      {
        id: 1,
        firstName: 'Alice',
        lastName: 'Johnson',
        answer: EventParticipationAnswer.YES,
        description: 'Entrepreneur and founder of a tech startup.',
        picture: 'https://example.com/alice_johnson.jpg',
      },
      {
        id: 2,
        firstName: 'Bob',
        lastName: 'Brown',
        answer: EventParticipationAnswer.NO,
        description: 'Investor looking for promising startups.',
        picture: 'https://example.com/bob_brown.jpg',
      },
      {
        id: 3,
        firstName: 'Charlie',
        lastName: 'White',
        answer: EventParticipationAnswer.PENDING,
        description: 'Product manager with a focus on user experience.',
        picture: 'https://example.com/charlie_white.jpg',
      },
    ],
  },
  {
    id: 3,
    communityId: 103,
    title: 'Marketing Workshop',
    memberCount: 30,
    description: 'A workshop for learning modern marketing strategies.',
    location: 'Los Angeles',
    startTime: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
    type: EventType.OTHER,
    isDatePicker: false,
    participants: [
      {
        id: 1,
        firstName: 'David',
        lastName: 'Garcia',
        answer: EventParticipationAnswer.MAYBE,
        description: 'Digital marketing expert specializing in social media.',
        picture: 'https://example.com/david_garcia.jpg',
      },
    ],
  },
  {
    id: 4,
    communityId: 104,
    title: 'Startup Pitch Night',
    memberCount: 50,
    description: 'An event for startups to pitch their ideas to investors.',
    location: 'Boston',
    startTime: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
    type: EventType.OTHER,
    isDatePicker: false,
    participants: [
      {
        id: 1,
        firstName: 'Eve',
        lastName: 'Martinez',
        answer: EventParticipationAnswer.YES,
        description: 'Startup founder and CEO of an AI company.',
        picture: 'https://example.com/eve_martinez.jpg',
      },
      {
        id: 2,
        firstName: 'Frank',
        lastName: 'Clark',
        answer: EventParticipationAnswer.NO,
        description: 'Venture capitalist interested in tech startups.',
        picture: 'https://example.com/frank_clark.jpg',
      },
      {
        id: 3,
        firstName: 'Grace',
        lastName: 'Hall',
        answer: EventParticipationAnswer.YES,
        description: 'Engineer working on sustainable energy solutions.',
        picture: 'https://example.com/grace_hall.jpg',
      },
    ],
  },
  {
    id: 5,
    communityId: 105,
    title: 'Design Thinking Seminar',
    memberCount: 25,
    description: 'A seminar on applying design thinking in community.',
    location: 'Chicago',
    startTime: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
    type: EventType.OTHER,
    isDatePicker: false,
    participants: [
      {
        id: 1,
        firstName: 'Hannah',
        lastName: 'Lopez',
        answer: EventParticipationAnswer.YES,
        description: 'Designer with expertise in user-centered design.',
        picture: 'https://example.com/hannah_lopez.jpg',
      },
      {
        id: 2,
        firstName: 'Ian',
        lastName: 'Kim',
        answer: EventParticipationAnswer.MAYBE,
        description: 'Community strategist focused on innovation.',
        picture: 'https://example.com/ian_kim.jpg',
      },
    ],
  },
];

export default function EventReportsTab() {
  const { t } = useTranslation('', { keyPrefix: 'page.agenda.report' });
  const { t: tGeneral } = useTranslation();
  const events = useEvents(EventViewType.ALL);

  const dispatch = useDispatch();
  const community = useSelectedCommunity();
  const [attendanceType, setAttendanceType] = useState<'PER_USER' | 'PER_EVENT'>('PER_EVENT');

  const [startDate, setStartDate] = useState<Date | undefined>(
    new Date(Date.now() - 1000 * 60 * 60 * 24 * 90), // today - 90 days
  );
  const [endDate, setEndDate] = useState<Date | undefined>(new Date(Date.now()));

  useEffect(() => {
    if (!community || !community.id) return;

    if (!community.features?.PARTICIPATION_INSIGHTS) {
      dispatch(setEvents({ events: MOCK_EVENTS as unknown as import('../../../types/event').Event[], communityId: community.id }));
      return;
    }

    if (!startDate || !endDate) return;

    eventService.getEventAnalytics(startDate, endDate);
  }, [startDate, endDate, community]);

  const handleDownload = () => {
    if (!startDate || !endDate) return;

    downloadAnalytics(
      events,
      `${t('title')}_${tGeneral('general.from')}_${formatDateForFileName(startDate)}_${tGeneral('general.to')}_${formatDateForFileName(endDate)}`,
    );
  };

  return (
    <main
      className={classNames('relative flex flex-col gap-4 overflow-x-hidden', {
        'h-[80vh] overflow-hidden': !community?.features?.PARTICIPATION_INSIGHTS,
      })}>
      {!community?.features?.PARTICIPATION_INSIGHTS && (
        <PrimaryOverlay>
          <PremiumRequiredCard />
        </PrimaryOverlay>
      )}
      <div className="flex flex-col rounded-xl bg-secondary-200 bg-opacity-50 px-8 py-3">
        <div className="flex items-center justify-between max-md:flex-col-reverse">
          <h3 className="font-semibold">{t('currentEventsPerPeriod')}</h3>
          <Button onClick={handleDownload}>
            {t('exportButton')}
            <TbDownload />
          </Button>
        </div>
        <div className="flex items-center justify-between gap-2 max-md:flex-col">
          <p className="text-xl font-semibold">
            {events.length} {t(`event${events.length === 1 ? '' : 's'}`).toLowerCase()}
          </p>
          <div className="flex gap-4 [&_.MuiInputBase-root]:pr-0 [&_input]:bg-primary-900 [&_input]:text-secondary-50">
            <Labeled label={tGeneral('general.from')}>
              <DatePicker date={startDate} setDate={setStartDate} includeTime={false} />
            </Labeled>
            <Labeled label={tGeneral('general.to')}>
              <DatePicker date={endDate} setDate={setEndDate} includeTime={false} />
            </Labeled>
          </div>
        </div>
      </div>
      {events.length === 0 ? (
        <div className="mt-4 self-center rounded-lg bg-secondary px-8 py-2">
          <p className="flex items-center gap-2">
            <FaInfoCircle />
            {t('noEvents')}
          </p>
        </div>
      ) : (
        <>
          <section className="flex gap-4 max-md:flex-col">
            <AnswerPercentageGraph events={events} />
            <Card className="flex-[2] items-start justify-between bg-secondary-200 bg-opacity-50">
              <h3 className="text-xl">{t('podiumTitle')}</h3>
              <PodiumGraph events={events} />
            </Card>
          </section>
          <Card className="w-full items-start justify-between bg-secondary-200 bg-opacity-50">
            <div className="flex w-full items-center justify-between gap-2 max-sm:flex-col">
              <h3 className="text-xl">
                {attendanceType === 'PER_EVENT' ? t('answersPerEvent') : t('answersPerUser')}
              </h3>
              <TabButtons
                options={[
                  { label: t('perEvent'), value: 'PER_EVENT' as const },
                  { label: t('perUser'), value: 'PER_USER' as const },
                ]}
                onTabChange={setAttendanceType}
              />
            </div>
            <AttendanceGraph events={events} type={attendanceType} />
          </Card>
        </>
      )}
    </main>
  );
}
